import {_} from 'lodash';
import * as ko from "knockout";
import {ObjectTypeId, TEXT_WRAPPER_END, TEXT_WRAPPER_START} from "../../constants";
import pubsub from "../../pubsub";
import {ISyncField} from "../../index";
import {IRectCss, ISyncfieldParameterObject, ITextRange, TSort} from "./index";
import {config, global, lm_} from "../../appContext";
import {nsDynamicVariable} from "./nsDynamicVariable";
import {getObjectDefinition, IObjectDefinition} from "../../util/misc";
import {PLEASE_WAIT_MESSAGE} from "../../constants/Language";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import getSyncfieldParameterDialogObject = nsDynamicVariable.getSyncfieldParameterDialogObject;
import getParameterIndex = nsDynamicVariable.getParameterIndex;
import listHtml from './list.html?raw';

export let template = listHtml;

interface IParams {
    id: string
}

export class viewModel {
    private data!: DataByAlphabet | DataByObjectType;
    private objectTypeId!: KnockoutComputed<ObjectTypeId>;
    private objectTypeIds!: KnockoutComputed<ObjectTypeId[]>;
    private onParameterHandler!: (syncfield: ISyncField, value: string, index: number) => void;
    private onSelectedHandler!: (syncfield: ISyncField) => void;
    private readonly currentIndex: KnockoutObservable<number> = ko.observable(0);
    private readonly isReady: KnockoutObservable<boolean> = lm_.isReady
    private readonly messageData: KnockoutObservable<string | null> = ko.observable(null);
    private readonly needle: KnockoutObservable<string | null> = ko.observable(null);
    private readonly styles: KnockoutObservable<IRectCss | null> = ko.observable(null);
    private readonly textRange: KnockoutObservable<ITextRange | null> = ko.observable(null);
    private sort!: TSort;
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    private readonly visibleItems: KnockoutObservableArray<ISyncField> = ko.observableArray<ISyncField>([]);
    private readonly listItems = ko.pureComputed((): ISyncField[] => this.visibleItems());

    constructor(private params: IParams) {
        pubsub.trigger('viewModel.initialized', {id: params.id, vm: this});
        this.kosub.subscribe(lm_.isReady, (bool: boolean) => {
            if (bool) {
                this.initSyncfields()
            }
        })
        this.kosub.subscribe(this.needle, () => {
            if (this.needle() !== null) {
                this.resetVisibleItems();
            }
        });
    }

    private getPageSize(): number {
        return config.DYNAMIC_VARIABLE_LIST_PAGE_SIZE ?? 40;
    }

    private getScrollThreshold(): number {
        return config.DYNAMIC_VARIABLE_LIST_SCROLL_THRESHOLD ?? 20;
    }

    /** Distance from the scroll bottom (px) at which the next page is requested. */
    private getScrollPixelThreshold(): number {
        const estimatedRowHeightPx = 32;
        return this.getScrollThreshold() * estimatedRowHeightPx;
    }

    /** Loads the first page of filtered items into the observable list. */
    private resetVisibleItems(): void {
        const filtered = this.dataProcessed();
        if (!filtered?.length) {
            this.visibleItems([]);
            return;
        }
        const pageSize = this.getPageSize();
        this.visibleItems(filtered.slice(0, Math.min(pageSize, filtered.length)));
    }

    /** Appends the next page when the user scrolls near the end of the loaded list. */
    private appendNextInterval(): void {
        const filtered = this.dataProcessed();
        if (!filtered?.length) {
            return;
        }
        const loadedCount = this.visibleItems().length;
        if (loadedCount >= filtered.length) {
            return;
        }
        const pageSize = this.getPageSize();
        const next = filtered.slice(loadedCount, Math.min(loadedCount + pageSize, filtered.length));
        this.visibleItems.push(...next);
    }

    /** Ensures keyboard selection exists in the DOM (extends loaded prefix as needed). */
    private ensureLoadedThroughIndex(index: number): void {
        const filtered = this.dataProcessed();
        if (!filtered?.length || index < 0) {
            return;
        }
        while (this.visibleItems().length <= index && this.visibleItems().length < filtered.length) {
            this.appendNextInterval();
        }
    }

    private getId(): string {
        return this.params.id + '-list';
    }

    public init(objectTypeId: KnockoutComputed<ObjectTypeId>, objectTypeIds: KnockoutComputed<ObjectTypeId[]>, onSelectedHandler: (syncfield: ISyncField) => void, onParameterHandler: (syncfield: ISyncField, value: any, index: number) => void, sort: TSort = 'alphabetically') {
        this.objectTypeId = objectTypeId;
        this.objectTypeIds = objectTypeIds;
        this.onSelectedHandler = onSelectedHandler;
        this.onParameterHandler = onParameterHandler;
        this.sort = sort;
        this.initSyncfields();
    }

    public initObjectTypeIds(objectTypeId: KnockoutComputed<ObjectTypeId>, objectTypeIds: KnockoutComputed<ObjectTypeId[]>) {
        this.objectTypeId = objectTypeId;
        this.objectTypeIds = objectTypeIds;
        this.initSyncfields();
    }

    private initSyncfields() {
        this.messageData(PLEASE_WAIT_MESSAGE);
        const data = [this.objectTypeId(), ...this.objectTypeIds()]
            .reduce((arr: ISyncField[], objectTypeId: ObjectTypeId) => {
                const lang = lm_.getInstance(objectTypeId)
                if (!lang) {
                    return arr
                }
                return arr.concat(lang.translations())
            }, [])
            .filter(Boolean)
        const d = data.filter(o => {
            switch (o.objecttypeid) {
                case ObjectTypeId.FunctionAlias:
                    return !!o.displayname;
                default:
                    return true;
            }
        });
        switch (this.sort) {
            case 'alphabetically':
                this.data = new DataByAlphabet(d, this.needle);
                break;
            case 'type':
                this.data = new DataByObjectType(d, this.needle, [this.objectTypeId(), ...this.objectTypeIds()]);
                break;
        }
        this.messageData(null);
        if (this.needle() !== null) {
            this.resetVisibleItems();
        }
    }

    private getParameterIndex(): number {
        const textRange = this.textRange();
        return textRange ? getParameterIndex(textRange) : 0;
    }

    public isParameterDialogable(): boolean {
        const syncfield = this.getSyncfield();
        return syncfield ? !!this.getParameterObject(syncfield, this.getParameterIndex())?.dialog : false;
    }

    private getParameterObject(syncfield: ISyncField, index: number): ISyncfieldParameterObject {
        const obj = getSyncfieldParameterDialogObject(syncfield, this.getParameterIndex());
        let parameters = syncfield.parameters;
        if (parameters?.startsWith(TEXT_WRAPPER_START)) {
            parameters = parameters.substring(1);
        }
        if (parameters?.endsWith(TEXT_WRAPPER_END)) {
            parameters = parameters.substring(0, parameters.length - 1);
        }
        const selected = index == this.getParameterIndex();
        return {
            css: this.textRange() && selected ? 'selected' : null,
            dialog: obj?.parameterDialog,
            index: index,
            object: obj,
            selected: selected,
            syncfield: syncfield,
            text: parameters?.split(';')[index],
        }
    }

    public initParameterDialog(parameterObject: ISyncfieldParameterObject = this.getParameterObject(this.getSyncfield()!, this.getParameterIndex())) {
        this.show(null);
        const attribute = parameterObject.object?.attribute;
        const dialog = parameterObject.dialog;
        if (!attribute || !dialog) {
            return;
        }
        dialog.content.params.callback = (obj: any) => {
            this.onParameterHandler(parameterObject.syncfield, String(obj[attribute]), parameterObject.index);
        };
        global.dialogManager.addComponent(dialog);
    }

    public show(params: { textRange: ITextRange, styles: IRectCss } | null) {
        if (!params) {
            this.currentIndex(0);
            this.needle(null);
            this.styles(null);
            this.messageData(null);
            this.textRange(null);
            this.visibleItems([]);
            return;
        }

        const preserveSelection = this.needle() !== null && this.needle() === params.textRange.str;
        if (!preserveSelection) {
            this.currentIndex(0);
        }

        this.textRange(params.textRange);
        this.styles(params.styles);
        this.needle(params.textRange.str);
        this.resetVisibleItems();

        if (this.dataProcessed() === null) {
            return;
        }
        const processed = this.dataProcessed()!;
        if (processed.length) {
            this.messageData(null);
        } else {
            this.messageData('Ingen dynamiske variabler samsvarer med verdi.');
        }
    }

    private dataProcessed = ko.pureComputed((): ISyncField[] | null => {
        if (this.isReady()) {
            return this.data.getProcessed();
        } else {
            return null;
        }
    })

    getNumberOfItems(): number {
        return this.dataProcessed()?.length ?? 0;
    }

    indexInc() {
        const processed = this.dataProcessed();
        if (!processed?.length) {
            return;
        }
        const max = processed.length - 1;
        const num = Math.min(max, this.currentIndex() + 1);
        this.currentIndex(num);
        this.ensureLoadedThroughIndex(num);
        this.scrollIntoView();
    }

    indexDec() {
        const num = Math.max(0, this.currentIndex() - 1);
        this.currentIndex(num);
        this.scrollIntoView();
    }

    onListScroll(_vm: viewModel, e: Event): void {
        const el = e.currentTarget as HTMLElement;
        const filtered = this.dataProcessed();
        if (!filtered?.length || this.visibleItems().length >= filtered.length) {
            return;
        }
        const pixelThreshold = this.getScrollPixelThreshold();
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - pixelThreshold) {
            this.appendNextInterval();
        }
    }

    private scrollIntoView() {
        const path = `#${this.getId()} .dynamic-input-list-item.selected`;
        const el = document.querySelector(path);
        if (el) {
            el.scrollIntoView({
                block: 'nearest',
            });
        }
    }

    getSyncfield(): ISyncField | undefined {
        return this.dataProcessed()?.[this.currentIndex()];
    }

    private onClick(syncfield: ISyncField, e: JQuery.Event) {
        this.onSelectedHandler(syncfield);
    }

    onMouseMoveAtIndex(index: number): void {
        this.currentIndex(index);
    }

    private onMouseDown(syncfield: ISyncField, e: JQuery.Event) {
        e.preventDefault();
    }

    private getSyncFieldDisplayName = getSyncFieldDisplayName.bind(this);

    private getObjDef(syncfield: ISyncField): IObjectDefinition | null {
        return getObjectDefinition(syncfield.objecttypeid);
    }

    dispose() {
        this.kosub.unsuscribeAll()
    }

}

abstract class AbstractData {

    protected data!: ISyncField[];

    constructor(protected needle: KnockoutObservable<string | null>) {
    }

    public abstract getProcessed(): ISyncField[]

    protected getFiltered() {
        const needle = this.needle();
        if (needle === null) {
            return [];
        } else if (needle === '') {
            return this.data;
        } else {
            let _needle = needle.toLowerCase();
            const index = _needle.indexOf(';'); // index of first parameter
            if (index > -1) {
                // search for the function name
                _needle = _needle.substring(0, index + 1);
                return this.data.filter((item: ISyncField) => getSyncFieldDisplayName(item).toLowerCase().indexOf(_needle) == 1);
            }
            return this.data.filter((item: ISyncField) => getSyncFieldDisplayName(item).toLowerCase().indexOf(_needle) > -1);
        }
    }

}

class DataByAlphabet extends AbstractData {

    constructor(data: ISyncField[], needle: KnockoutObservable<string | null>) {
        super(needle);
        this.data = this.getInitialSort(data);
    }

    protected getInitialSort(data: ISyncField[]): ISyncField[] {
        return _(data)
            .sortBy([
                (s: ISyncField) => getSyncFieldDisplayName(s),
                (s: ISyncField) => s.name.charAt(0) == TEXT_WRAPPER_START ? 1 : -1,
            ])
            .value();
    }

    public getProcessed(): ISyncField[] {
        return this.getFiltered();
    }
}

class DataByObjectType extends AbstractData {

    constructor(data: ISyncField[], needle: KnockoutObservable<string | null>, protected objectTypeIds: ObjectTypeId[]) {
        super(needle);
        this.data = this.getInitialSort(data);
    }

    protected getInitialSort(data: ISyncField[]): ISyncField[] {
        return _(data)
            .sortBy([
                (s: ISyncField) => this.objectTypeIds.indexOf(s.objecttypeid),
                (s: ISyncField) => s.name.charAt(0) == TEXT_WRAPPER_START ? 1 : -1,
                (s: ISyncField) => getSyncFieldDisplayName(s),
            ])
            .value();
    }

    getProcessed(): ISyncField[] {
        return this.getFiltered();
    }

}

const getSyncFieldDisplayName = (item: ISyncField): string => {
    let ret: string = '';
    if (item.name.charAt(0) == TEXT_WRAPPER_START) {
        ret = item.displayname || item.name;
    } else {
        if (item.displayname) {
            ret += `${item.displayname} (${item.name})`
        } else {
            ret = item.name;
        }
    }
    return ret;
}